import { ButtonSlim, Flex } from '@Calix-Commerce/design-system';
import { FormContainer, Textarea, Title } from './styledComponents';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authenticationState, loadingState, notificationState } from 'state/atoms';
import { useAppNavigation, useNotifyError } from 'utils/hooks';
import { SEND_CONTACT_MESSAGE } from 'connectors/mutations';
import { useApolloClient, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { GET_SUPPORT_ACCESS_QUOTE } from 'connectors/queries/quote';
import { getQuoteId } from 'utils/helpers/quote';

const REQUEST_SUPPORT_MESSAGE =
  'Use this form to communicate with your Customer Operations Representative regarding support for your quote.';

export const SupportRequest = () => {
  const authentication = useRecoilValue(authenticationState);
  const [globalLoading, setGlobalLoading] = useRecoilState(loadingState);
  const setNotification = useSetRecoilState(notificationState);
  const notifyError = useNotifyError({ fallbackMessage: 'Unable to send message.' });
  const [sendContactMessage] = useMutation(SEND_CONTACT_MESSAGE);
  const [text, setText] = useState(``);
  const [messageSent, setMessageSent] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
  const apolloClient = useApolloClient();
  const { redirectToQuoteList } = useAppNavigation();

  const param = useParams();
  const transactionId = param.transactionId;

  useEffect(() => {
    const checkQuoteAccess = async () => {
      setGlobalLoading(true);

      const getQuoteResponse = await apolloClient
        .query({
          query: GET_SUPPORT_ACCESS_QUOTE,
          variables: {
            transactionId,
            accountId: authentication.endCustomerAccountId,
            accessContext: 'support',
          },
        })
        .catch(() => {
          setQuoteNumber(null);
        })
        .finally(() => setGlobalLoading(false));

      const quoteData = getQuoteResponse?.data?.getAccount?.getQuote || null;

      if (quoteData) {
        setQuoteNumber(getQuoteId(quoteData));
      }
    };

    checkQuoteAccess();
  }, [
    apolloClient,
    authentication.endCustomerAccountId,
    setNotification,
    transactionId,
    setGlobalLoading,
  ]);

  const notifyEmptyMessage = () => {
    setNotification({
      type: 'error',
      show: true,
      text: 'Please insert a message to send to your Customer Operations Representative',
      timeout: null,
    });
  };

  const handleAccessRequest = async (message: string) => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      notifyEmptyMessage();
    } else {
      setGlobalLoading(true);
      await sendContactMessage({
        variables: {
          messageInput: {
            accountId: authentication.endCustomerAccountId,
            recipientType: 'CustomerOperations',
            type: 'MyCalixStoreSupport',
            content: `Quote Number - ${quoteNumber}: ${trimmedMessage}`,
          },
        },
      })
        .then(() => {
          setMessageSent(true);
        })
        .catch(notifyError)
        .finally(() => setGlobalLoading(false));
    }
  };

  if (globalLoading) {
    return null;
  }

  if (!quoteNumber) {
    return (
      <FormContainer direction="column" alignment="center">
        <Title>Request Support for Quote Transaction Id {transactionId}</Title>
        <p>The quote number provided is invalid or you do not have access to it.</p>
      </FormContainer>
    );
  }

  if (messageSent) {
    return (
      <FormContainer direction="column" alignment="center">
        <Title>Request Support for Quote #{quoteNumber}</Title>
        <p>Thank you! Your request has been sent to your Customer Operations Representative.</p>
        <ButtonSlim onClick={() => redirectToQuoteList({ replaceCurrentLocation: true })}>
          View Quotes
        </ButtonSlim>
      </FormContainer>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAccessRequest(text);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormContainer direction="column" alignment="center">
        <Title>Request Support for Quote #{quoteNumber}</Title>
        <>
          <p>{REQUEST_SUPPORT_MESSAGE}</p>
          <Flex direction="column" style={{ width: '100%' }} alignment="center">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} />
            <ButtonSlim type="submit">SEND</ButtonSlim>
          </Flex>
        </>
      </FormContainer>
    </form>
  );
};
